/**
 * Trang xem thử thẻ ca của người trang điểm — chỉ chạy ở bản phát triển
 * (`_dev` tự chặn ở bản phát hành).
 *
 * Vì sao đáng có: cửa nộp ảnh nghiệm thu trước/sau mở theo TRẠNG THÁI ĐƠN, mà
 * trạng thái đó đổi ngoài tầm tay người trang điểm — thợ chụp dán link giữa
 * buổi là đơn nhảy sang "sửa hình" ngay. Bài kiểm khẳng định được "có hiện",
 * nhưng không thay được việc nhìn tận mắt hai trạng thái cạnh nhau.
 *
 * Dữ liệu ở đây là dữ liệu dựng sẵn nạp thẳng vào bộ nhớ đệm — thành phần
 * hiển thị là THẬT, không phải bản vẽ lại.
 */
import { useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { PageHeader } from "@/components/PageHeader";
import { OverdueProofBanner } from "@/features/ekip-schedule/EkipSchedule";
import type { EkipShift } from "@/features/ekip-schedule/api";
import { ScheduleEkipAssign } from "@/features/schedule/components/detail/ScheduleEkipAssign";
import type { Order } from "@/features/orders/api";
import { BRANDS_KEY } from "@/features/products/catalogApi";
import { ProofsSection } from "@/features/proofs/ProofsSection";
import type { OrderProof } from "@/features/proofs/utils";
import { useAuthV2 } from "@/lib/authv2/store";

export const Route = createFileRoute("/_dev/ekip-proof")({
  component: EkipProofDemo,
});

const TOI = { id: "mk1", name: "Mai (trang điểm)" };

function don(id: string, status: string): Order {
  return {
    id,
    code: "PF-1001",
    customer: "Chị Lan",
    status,
    branch: "sg_32",
    product: "cdb01",
    category: "profile",
    shootDate: "2026-09-15",
    photographers: [{ id: "ph1", name: "Nam (thợ chụp)" }],
    makeups: [TOI],
    retouchers: [],
  } as unknown as Order;
}

const CA_DANG_CHUP = don("o-shooting", "shooting");
const CA_DA_SANG_SUA_HINH = don("o-editing", "editing");

/** Hôm nay theo máy xem — đơn ảnh thẻ đúng ngày chụp. */
function homNay(): string {
  const d = new Date();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  return `${d.getFullYear()}-${m}-${`${d.getDate()}`.padStart(2, "0")}`;
}

/** Ảnh thẻ còn "Đã đặt", đúng ngày chụp — cửa sớm của QĐ 0261. */
const CA_ANH_THE_DA_DAT = {
  ...don("o-idphoto-booked", "booked"),
  code: "AT-1002",
  category: "idphoto",
  product: "at_cv",
  shootDate: homNay(),
} as Order;

/** Ca hôm qua, đơn đã xong, còn 6 tiếng nữa là hết hạn nộp ảnh. */
const CA_HOM_QUA: EkipShift = {
  id: "o-hom-qua",
  customer: "Chị Hôm Qua",
  status: "completed",
  category: "profile",
  product: "cdb01",
  branch: "sg_32",
  shootDate: "2026-09-14",
  startTime: "09:00",
  endTime: "11:00",
  total: 0,
  deposit: 0,
  payableTotal: 0,
  surchargesTotal: 0,
  paid: 0,
  refunded: 0,
  isKol: false,
  tags: [],
  isMine: true,
  isPhoto: false,
  myAccept: "2026-09-14T02:00:00Z",
  makeupProofDueAt: new Date(Date.now() + 6 * 60 * 60 * 1000).toISOString(),
} as unknown as EkipShift;

/** Ca thứ năm: đã gửi nhiều lượt, hai tấm trước + một tấm sau, còn gỡ được. */
const CA_NHIEU_ANH = don("o-nhieu-anh", "shooting");

// Ảnh mẫu tại máy (`data:`) — trang xem thử không gọi ra kho ảnh thật.
const mau = (mau1: string, mau2: string) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="800" height="1000"><rect width="800" height="1000" fill="${mau1}"/><circle cx="400" cy="420" r="220" fill="${mau2}"/></svg>`,
  )}`;

function bangChung(
  id: string,
  kind: OrderProof["kind"],
  who: { id: string; name: string },
  images: string[],
  createdAt: string,
  removable: boolean,
): OrderProof {
  return {
    id,
    orderId: CA_NHIEU_ANH.id,
    kind,
    images,
    takenBy: who.id,
    takenByName: who.name,
    createdAt,
    face: 1,
    removable,
  };
}

const KHAC = { id: "mk2", name: "Hà (trang điểm)" };
const BANG_CHUNG_NHIEU_ANH: OrderProof[] = [
  bangChung("p1", "makeup_before", TOI, [mau("#e9d7c9", "#c9a58c"), mau("#e5d0c0", "#b98f75")], "2026-09-15T02:10:00Z", true),
  bangChung("p2", "makeup_before", TOI, [mau("#eadbd0", "#c49a82")], "2026-09-15T02:14:00Z", true),
  bangChung("p3", "makeup_after", TOI, [mau("#f3d9d4", "#d98a86")], "2026-09-15T03:20:00Z", true),
  bangChung("p4", "makeup_before", KHAC, [mau("#dcd3cb", "#a88f7c")], "2026-09-15T02:12:00Z", false),
  bangChung("p5", "makeup_after", KHAC, [mau("#f0d3cf", "#c77a78")], "2026-09-15T03:25:00Z", false),
];

/** Bộ nhớ đệm dựng sẵn: tôi đã nhận ca, chưa nộp tấm ảnh nào. */
function seededClient(): QueryClient {
  const qc = new QueryClient({ defaultOptions: { queries: { retry: false, staleTime: Infinity } } });
  qc.setQueryData(["proofs", CA_HOM_QUA.id], []);
  // Danh mục dòng: chỉ ảnh thẻ bật "được xếp là nhận ca" (QĐ 0234).
  qc.setQueryData(BRANDS_KEY, {
    brands: [],
    lines: [
      { code: "idphoto", name: "Ảnh thẻ", sortOrder: 1, crewAcceptOnAssign: true },
      { code: "profile", name: "Profile", sortOrder: 2, crewAcceptOnAssign: false },
    ],
  });
  qc.setQueryData(["ekip", "confirms", CA_ANH_THE_DA_DAT.id], {});
  qc.setQueryData(["proofs", CA_ANH_THE_DA_DAT.id], []);
  for (const o of [CA_DANG_CHUP, CA_DA_SANG_SUA_HINH, CA_NHIEU_ANH]) {
    qc.setQueryData(["ekip", "confirms", o.id], {
      [`${TOI.id}_accept`]: { name: TOI.name, at: "2026-09-15T02:00:00Z" },
    });
    qc.setQueryData(["proofs", o.id], []);
  }
  qc.setQueryData(["proofs", CA_NHIEU_ANH.id], BANG_CHUNG_NHIEU_ANH);
  return qc;
}

function EkipProofDemo() {
  useEffect(() => {
    useAuthV2.setState({ currentStaff: TOI as never });
  }, []);

  return (
    <QueryClientProvider client={seededClient()}>
      <div className="space-y-6 p-4">
        <PageHeader title="Xem thử · Ô ảnh trước/sau của người trang điểm" />
        <p className="text-sm text-muted-foreground">
          Cùng một người trang điểm đã nhận ca. Khác nhau đúng một thứ: trạng thái đơn.
        </p>

        <section className="space-y-2">
          <h2 className="text-sm font-bold">1. Đơn “đang chụp”</h2>
          <ScheduleEkipAssign order={CA_DANG_CHUP} />
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-bold">
            2. Đơn đã sang “sửa hình” (thợ chụp vừa dán link)
          </h2>
          <ScheduleEkipAssign order={CA_DA_SANG_SUA_HINH} />
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-bold">
            3. Ảnh thẻ còn “đã đặt lịch”, đúng ngày chụp — nộp đủ ảnh + bấm hoàn tất là đơn sang Đang chụp
          </h2>
          <ScheduleEkipAssign order={CA_ANH_THE_DA_DAT} />
        </section>

        <section className="space-y-2">
          <h2 className="text-sm font-bold">
            4. Ca HÔM QUA, đơn đã xong — khối nhắc nợ ảnh trên trang Lịch ê-kíp
          </h2>
          <OverdueProofBanner shifts={[CA_HOM_QUA]} />
        </section>

        <section className="space-y-2" data-testid="canh-nhieu-anh">
          <h2 className="text-sm font-bold">
            5. Đã gửi nhiều lượt — thẻ ca hiện đủ mọi tấm, tách trước / sau, có nút xoá
          </h2>
          <ScheduleEkipAssign order={CA_NHIEU_ANH} />
        </section>

        <section className="space-y-2" data-testid="canh-chi-tiet-don">
          <h2 className="text-sm font-bold">
            6. Chi tiết đơn · thẻ Ekip · mục “Bằng chứng” — hai người trang điểm
          </h2>
          <ProofsSection orderId={CA_NHIEU_ANH.id} />
        </section>
      </div>
    </QueryClientProvider>
  );
}
